import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import OtpModal from "../common/OtpModal";
import { TextField } from "../common/inputFiled";
import { validateEmail } from "../../utils/validateEmail";
import { appStorage } from "../../utils/appStorage";
import { forceAuthLogout } from "../../utils/authSession";
import { APP_SETUP_STEPS } from "../../config/appSetupConfig";
import {
  appSetupSignup,
  submitBasicPlanPayment,
  resendEmailOtp,
  verifyEmailOtp,
  resendPhoneOtp,
  verifyPhoneOtp,
  submitPersonalInfo,
  submitEducation,
  registerWaitlistPlatform,
} from "../../utils/appSetupApi";
import {
  startAppSetupWizard,
  finishAppSetupWizard,
  getAppSetupStep,
  setAppSetupStep,
  saveAppSetupSignupDraft,
  getAppSetupSignupDraft,
} from "../../utils/appSetupWizardStorage";
import AppSetupSignupStep from "./appSetup/AppSetupSignupStep";
import AppSetupPersonalInfoStep from "./appSetup/AppSetupPersonalInfoStep";
import AppSetupEducationStep from "./appSetup/AppSetupEducationStep";

const PAGE_SHELL = "mobile-scroll-y h-[var(--app-height,100dvh)] bg-[#F3F0FF] px-4 py-6 sm:py-8";

const PLATFORMS = [
  { id: "android", label: "Android", description: "Google Play Store", icon: "/playstore.svg" },
  { id: "ios", label: "iOS", description: "Apple App Store", icon: "/appstore.svg" },
];

function PlatformCard({ platform, selected, onSelect }) {
  const isSelected = selected === platform.id;
  return (
    <button
      type="button"
      onClick={() => onSelect(platform.id)}
      aria-pressed={isSelected}
      className={[
        "group relative flex flex-1 flex-col items-center gap-3 rounded-2xl border-2 p-5 transition-all duration-200",
        "focus:outline-none focus-visible:ring-4 focus-visible:ring-violet-500/20",
        isSelected
          ? "border-[#7C4DFF] bg-gradient-to-b from-violet-50 to-white shadow-lg shadow-violet-200/60"
          : "border-[#E6E2FF] bg-white hover:border-violet-300 hover:shadow-md",
      ].join(" ")}
    >
      {isSelected && (
        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-b from-[#8862F2] to-[#7544FC] text-white shadow-sm">
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
      )}
      <div className={["flex h-14 w-14 items-center justify-center rounded-2xl transition-transform duration-200", isSelected ? "scale-105 bg-[#7C4DFF] shadow-md ring-1 ring-violet-100" : "bg-[#7C4DFF] group-hover:scale-105"].join(" ")}>
        <img src={platform.icon} alt="" className="h-10 w-10 object-contain" />
      </div>
      <div className="text-center">
        <p className={`text-base font-semibold ${isSelected ? "text-[#643ECF]" : "text-gray-800"}`}>{platform.label}</p>
        <p className="mt-0.5 text-xs text-gray-500">{platform.description}</p>
      </div>
    </button>
  );
}

function StepIndicator({ currentStep }) {
  const steps = [
    { key: APP_SETUP_STEPS.SIGNUP, label: "Sign up" },
    { key: APP_SETUP_STEPS.PERSONAL_INFO, label: "Profile" },
    { key: APP_SETUP_STEPS.EDUCATION, label: "Education" },
    { key: APP_SETUP_STEPS.PLATFORM, label: "Device" },
  ];
  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="mx-auto mb-6 flex max-w-[520px] items-center justify-center gap-2 px-4">
      {steps.map((step, index) => {
        const done = currentIndex > index;
        const active = currentIndex === index;
        return (
          <div key={step.key} className="flex items-center gap-2">
            <div
              className={[
                "flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold",
                done || active ? "bg-[#6938EF] text-white" : "bg-gray-200 text-gray-500",
              ].join(" ")}
            >
              {done ? "✓" : index + 1}
            </div>
            {index < steps.length - 1 && (
              <div className={`h-0.5 w-6 sm:w-10 ${done ? "bg-[#6938EF]" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AppSetupPage() {
  const navigate = useNavigate();
  const [step, setStepState] = useState(() => getAppSetupStep());
  const [signupDraft, setSignupDraft] = useState(() => getAppSetupSignupDraft());
  const [loading, setLoading] = useState(false);

  const [emailOtpOpen, setEmailOtpOpen] = useState(false);
  const [mobileOtpOpen, setMobileOtpOpen] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const [platform, setPlatform] = useState("");
  const [platformEmail, setPlatformEmail] = useState("");
  const [otpContactEmail, setOtpContactEmail] = useState("");
  const [otpContactPhone, setOtpContactPhone] = useState("");
  const [platformErrors, setPlatformErrors] = useState({});

  const setStep = useCallback((nextStep) => {
    setStepState(nextStep);
    setAppSetupStep(nextStep);
  }, []);

  useEffect(() => {
    startAppSetupWizard();

    const savedStep = getAppSetupStep();
    const hasToken = Boolean(Cookies.get("auth_token"));
    const draft = getAppSetupSignupDraft();

    if (savedStep !== APP_SETUP_STEPS.SIGNUP && !hasToken) {
      setStep(APP_SETUP_STEPS.SIGNUP);
      toast.error("Session expired. Please sign up again.");
      return;
    }

    if (savedStep === APP_SETUP_STEPS.PERSONAL_INFO && draft) {
      setSignupDraft(draft);
    }

    if (savedStep === APP_SETUP_STEPS.PLATFORM || savedStep === APP_SETUP_STEPS.COMPLETE) {
      const email = appStorage.getItem("email") || draft?.email || "";
      if (email) setPlatformEmail(email);
    }

    const email = appStorage.getItem("email") || draft?.email || "";
    const phone = appStorage.getItem("phone_number") || draft?.phoneNumber || "";
    const code = appStorage.getItem("country_code") || draft?.countryCode || "";
    if (email) setOtpContactEmail(email);
    if (phone) setOtpContactPhone(`${code} ${phone}`.trim());
  }, [setStep]);

  const goToPersonalInfo = useCallback(() => {
    setStep(APP_SETUP_STEPS.PERSONAL_INFO);
  }, [setStep]);

  const handleSignupSubmit = async (formData) => {
    setLoading(true);
    try {
      await appSetupSignup(formData);
      const draft = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        countryCode: formData.countryCode,
        phoneNumber: formData.phoneNumber,
      };
      setSignupDraft(draft);
      saveAppSetupSignupDraft(draft);
      const normalizedEmail = formData.email.trim().toLowerCase();
      setPlatformEmail(normalizedEmail);
      setOtpContactEmail(normalizedEmail);
      setOtpContactPhone(`${formData.countryCode} ${formData.phoneNumber}`.trim());

      try {
        await submitBasicPlanPayment();
      } catch (err) {
        toast.error(err.message || "Could not activate Basic plan.");
        return;
      }

      try {
        await resendEmailOtp(normalizedEmail);
      } catch (err) {
        toast.error(err.message || "Could not send email code. Try verifying manually.");
      }

      setEmailOtpOpen(true);
      toast.success("Account created with Basic plan! Verify your email to continue.");
    } catch (err) {
      toast.error(err.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailOtp = async (otp) => {
    const email = otpContactEmail || appStorage.getItem("email");
    if (!email) {
      toast.error("Email not found. Please sign up again.");
      return;
    }

    setOtpLoading(true);
    try {
      const result = await verifyEmailOtp(email, otp);
      setEmailOtpOpen(false);
      if (email) {
        setOtpContactEmail(email);
        appStorage.setItem("email", email);
        appStorage.setItem("user_email", email);
      }
      toast.success(result.message || "Email verified!");

      if (result.needsMobileOtp) {
        try {
          await resendPhoneOtp();
        } catch (err) {
          toast.error(err.message || "Could not send SMS code.");
        }
        setMobileOtpOpen(true);
        return;
      }

      goToPersonalInfo();
    } catch (err) {
      toast.error(err.message || "Invalid email OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyMobileOtp = async (otp) => {
    setOtpLoading(true);
    try {
      const result = await verifyPhoneOtp(otp);
      setMobileOtpOpen(false);
      toast.success(result.message || "Mobile verified!");
      goToPersonalInfo();
    } catch (err) {
      toast.error(err.message || "Invalid mobile OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handlePersonalInfoSubmit = async (payload) => {
    setLoading(true);
    try {
      await submitPersonalInfo(payload);
      toast.success("Personal info saved!");
      setStep(APP_SETUP_STEPS.EDUCATION);
    } catch (err) {
      toast.error(err.message || "Failed to save personal info");
    } finally {
      setLoading(false);
    }
  };

  const handleEducationSubmit = async (educationData, isCurrentStudying) => {
    setLoading(true);
    try {
      await submitEducation(educationData, isCurrentStudying);
      toast.success("Education saved!");
      setStep(APP_SETUP_STEPS.PLATFORM);
    } catch (err) {
      toast.error(err.message || "Failed to save education");
    } finally {
      setLoading(false);
    }
  };

  const handlePlatformSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!platform) nextErrors.platform = "Please select Android or iOS";

    const email = platformEmail || appStorage.getItem("email") || "";
    const emailResult = validateEmail(email);
    if (!emailResult.valid) nextErrors.email = emailResult.error;

    if (Object.keys(nextErrors).length > 0) {
      setPlatformErrors(nextErrors);
      return;
    }

    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      await registerWaitlistPlatform(normalizedEmail, platform);
      appStorage.setItem("email", normalizedEmail);
      appStorage.setItem("device_platform", platform);
      localStorage.setItem("device_platform", platform);
      localStorage.setItem("waitlist_email", normalizedEmail);
      setStep(APP_SETUP_STEPS.COMPLETE);
      toast.success("All set! Check your email for login details.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    finishAppSetupWizard();
    forceAuthLogout();
    navigate("/login", { replace: true });
  };

  if (step === APP_SETUP_STEPS.COMPLETE) {
    const email = platformEmail || appStorage.getItem("email") || "";
    return (
      <div className={PAGE_SHELL}>
        <div className="relative mx-auto w-full max-w-[520px] py-8 sm:py-12">
          <div className="w-full rounded-3xl border border-violet-100 bg-white p-8 text-center shadow-xl shadow-violet-200/40">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center text-white">
              <img src="./logo.svg" alt="" className="h-16 w-16" />
            </div>
            <h2 className="text-2xl font-bold text-[#212121]">You&apos;re all set!</h2>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-[#757575]">
              Your profile is complete. We&apos;ll notify you at{" "}
              <span className="font-semibold text-[#643ECF]">{email}</span> when PhoneBookPlus is ready for{" "}
              {platform === "android" ? "Android" : "iOS"}. Use the login link in your email to sign in anytime.
            </p>
            <button
              type="button"
              onClick={handleFinish}
              className="submit-button mt-8 flex w-full items-center justify-center rounded-xl py-3.5 text-sm font-semibold shadow-lg shadow-violet-500/25 transition hover:brightness-105"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <OtpModal
        open={emailOtpOpen}
        title="Verify Email"
        description="Enter the 6-digit code sent to your email address."
        contactLabel={otpContactEmail || appStorage.getItem("email") || ""}
        loading={otpLoading}
        onClose={() => setEmailOtpOpen(false)}
        onVerify={handleVerifyEmailOtp}
      />

      <OtpModal
        open={mobileOtpOpen}
        title="Verify Mobile"
        description="Enter the 6-digit SMS code sent to your mobile number."
        contactLabel={otpContactPhone || `${appStorage.getItem("country_code") || ""} ${appStorage.getItem("phone_number") || ""}`.trim()}
        loading={otpLoading}
        onClose={() => setMobileOtpOpen(false)}
        onVerify={handleVerifyMobileOtp}
      />

      {step !== APP_SETUP_STEPS.SIGNUP && step !== APP_SETUP_STEPS.COMPLETE && (
        <div className="pt-4">
          <StepIndicator currentStep={step} />
        </div>
      )}

      {step === APP_SETUP_STEPS.SIGNUP && (
        <AppSetupSignupStep onSubmit={handleSignupSubmit} loading={loading} />
      )}

      {step === APP_SETUP_STEPS.PERSONAL_INFO && signupDraft && (
        <AppSetupPersonalInfoStep
          signupDraft={signupDraft}
          onSubmit={handlePersonalInfoSubmit}
          loading={loading}
        />
      )}

      {step === APP_SETUP_STEPS.PERSONAL_INFO && !signupDraft && (
        <div className={PAGE_SHELL}>
          <div className="mx-auto max-w-md text-center text-sm text-gray-600">
            Session data missing. Please{" "}
            <button type="button" className="text-[#643ECF] underline" onClick={() => setStep(APP_SETUP_STEPS.SIGNUP)}>
              start signup again
            </button>
            .
          </div>
        </div>
      )}

      {step === APP_SETUP_STEPS.EDUCATION && (
        <AppSetupEducationStep onSubmit={handleEducationSubmit} loading={loading} />
      )}

      {step === APP_SETUP_STEPS.PLATFORM && (
        <div className={PAGE_SHELL}>
          <div className="relative mx-auto w-full max-w-[520px] px-4 pb-10 sm:px-8 md:px-10">
            <div className="mb-8 flex flex-col items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center text-white">
                <img src="./logo.svg" alt="" className="h-16 w-16" />
              </div>
              <div className="text-center">
                <h1 className="text-3xl font-semibold tracking-tight text-gray-500 sm:text-4xl">PhoneBookPlus</h1>
                <p className="mt-2 text-sm font-medium text-[#643ECF]">Choose your device</p>
              </div>
            </div>

            <div className="rounded-3xl border border-violet-100 bg-white/90 p-6 shadow-xl shadow-violet-200/30 backdrop-blur-sm sm:p-8">
              <form className="space-y-6" onSubmit={handlePlatformSubmit} noValidate>
                <div>
                  <label className="mb-3 block text-xs font-medium text-gray-600">
                    Select platform <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-3">
                    {PLATFORMS.map((item) => (
                      <PlatformCard
                        key={item.id}
                        platform={item}
                        selected={platform}
                        onSelect={(value) => {
                          setPlatform(value);
                          setPlatformErrors((prev) => ({ ...prev, platform: "" }));
                        }}
                      />
                    ))}
                  </div>
                  {platformErrors.platform && <p className="mt-2 text-xs text-red-500">{platformErrors.platform}</p>}
                </div>

                <TextField
                  id="confirm-email"
                  type="email"
                  name="email"
                  label="Confirm email address"
                  required
                  startIcon="./input-logos/sms.svg"
                  value={platformEmail || appStorage.getItem("email") || ""}
                  onChange={(e) => {
                    setPlatformEmail(e.target.value);
                    setPlatformErrors((prev) => ({ ...prev, email: "" }));
                  }}
                  error={platformErrors.email}
                  disabled
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="submit-button flex w-full items-center justify-center rounded-xl py-3.5 text-sm font-semibold shadow-lg shadow-violet-500/25 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Saving..." : "Finish Setup"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
